import React from "react";
import { Formik, FormikHelpers } from 'formik';
import { Alert, Box, Button, Grid, Group } from "@mantine/core";
import { useNavigate } from "react-router-dom";
import { notifications } from '@mantine/notifications';

import { buildFormConfig, FormConfig } from "src/utils/forms";

import { GenericError } from "src/state/_types";

import { Maybe } from "src/state/gql/_types";
import { changeEmptyPropsDeep } from "src/utils/objects";
import { FieldRenderer } from "src/components/forms/FieldRenderer"
import { Icon } from "@iconify/react/dist/iconify.js";
import { Icons } from "../ui/Icons";

type GenericDictionary = {
    [index: string]: any;
}

type GenericModel = {
    id: string;
    [index: string]: any;
}

type FormInlineProps<T extends GenericDictionary, M extends GenericModel, I extends any> = {
    actionTitle?: string;
    formSchema: any;
    createAction?(value: any): Promise<Maybe<Pick<M, 'id'>> | undefined>;
    bulkUpdateAction?(value: any): Promise<Maybe<Maybe<{ returning: M[] }>[]> | undefined>
    updateAction?(value: any): Promise<Maybe<M> | undefined>;
    deleteAction?(value: any): Promise<Maybe<Pick<M, 'id'>> | undefined>;
    resetAction?(): void;
    onClose?(item?: Pick<M, 'id'>): void;
    onClose?(items?: Maybe<Maybe<{ returning: M[]; }>[]> | undefined): void;
    isLoading: boolean;
    errors: GenericError[];
    currentItemId: I | null;
    initialData: T;
    currentItemData: T | null
    hideDelete?: boolean;
    actionColor?: string;
    requiredDirty?: boolean;
    redirectPath?: string;
    createRedirect?: boolean;
    updateRedirect?: boolean;
    deleteRedirect?: boolean;
    validators?: GenericDictionary;
    buttonColor?: string;
    buttonIcon?: string;
    buttonText?: string;
}

export type FormComponentProps<T extends GenericDictionary> = {
    actionTitle?: string | undefined;
    currentItem: T | null;
    onFormReset?(): void;
    onFormAction?(): void;
    onClose?(): void;
    preselectKey?: string;
    preselectValue?: any;
    actionColor?: string;
}


export function FormInline<PayloadType extends GenericDictionary, ModelType extends GenericModel, IDType extends string>({
    formSchema,
    createAction,
    updateAction,
    //deleteAction,
    resetAction,
    bulkUpdateAction,
    onClose,
    errors,
    currentItemId,
    currentItemData,
    initialData,
    //hideDelete = false,
    requiredDirty = true,
    redirectPath,
    createRedirect = false,
    updateRedirect = false,
    validators = {},
    buttonColor = "green.9",
    buttonIcon = "ph:floppy-disk-back-duotone",
    buttonText = "Save",
}: React.PropsWithChildren<FormInlineProps<PayloadType, ModelType, IDType>>): React.ReactElement | null {

    const navigate = useNavigate();
    const [formMode, setFormMode] = React.useState<'create' | 'update' | 'bulkUpdate' | null>(null);
    const [initialState] = React.useState<PayloadType>(currentItemData || initialData);
    const [formConfig, setFormConfig] = React.useState<FormConfig<ModelType> | null>(null)

    //if (!deleteAction) hideDelete = true

    React.useEffect(() => {
        if (currentItemData && formMode !== 'update') {
            setFormMode('update');
            let config = buildFormConfig<ModelType>(formSchema, validators, 'update')
            setFormConfig(config)
        } else if (!!bulkUpdateAction && formMode !== 'bulkUpdate') {
            setFormMode('bulkUpdate');
            let config = buildFormConfig<ModelType>(formSchema, validators, 'update')
            setFormConfig(config)
        } else if (!currentItemData && formMode !== 'create' && formMode !== 'bulkUpdate') {
            setFormMode('create');
            let config = buildFormConfig<ModelType>(formSchema, validators, 'create')
            setFormConfig(config)
        }
    }, [formMode, formSchema, validators, currentItemData, initialData]);

    // const openDeleteModal = () =>
    //     modals.openConfirmModal({
    //         title: 'Delete Item?',
    //         zIndex: 999,
    //         centered: true,
    //         children: (
    //             <Text size="sm">
    //                 Are you sure you want to delete this item? This action is destructive and cannot be undone.
    //             </Text>
    //         ),
    //         labels: { confirm: 'Delete', cancel: "No, Dont Delete" },
    //         confirmProps: { color: 'red' },
    //         onConfirm: () => handleDelete(),
    //     });

    const handleClose = async () => {
        if (currentItemData && resetAction) {
            resetAction();
        } else {
            if (onClose) {
                onClose()
            }
        }
    }

    // const handleDelete = async () => {
    //     if (formMode === 'update' && currentItemId && deleteAction) {
    //         let deletedItem = await deleteAction({ id: currentItemId });
    //         if (deletedItem) {
    //             // notifications.show({
    //             //     icon: <Icon icon={Icons.warningCircle} width={24} />,
    //             //     color: 'green',
    //             //     autoClose: 4000,
    //             //     title: 'Success!',
    //             //     message: `${formConfig?.title || 'Item'} Deleted`
    //             // })
    //             if (resetAction) resetAction();
    //             if (onClose) {
    //                 onClose(deletedItem)
    //                 if (deleteRedirect && redirectPath) {
    //                     navigate(redirectPath)
    //                 }
    //             }
    //         } else {
    //             // notifications.show({
    //             //     icon: <Icon icon={Icons.warningCircle} width={24} />,
    //             //     color: 'red',
    //             //     autoClose: 4000,
    //             //     title: 'Opps!',
    //             //     message: `Could Not Delete ${formConfig?.title || 'Item'}`
    //             // })
    //         }
    //     }
    // }

    const getEditablePayload = (payload: PayloadType): PayloadType => {
        let editablePayload: GenericDictionary = {};
        Object.keys(payload).forEach((payloadKey) => {
            if (formConfig && formConfig.editable.indexOf(payloadKey) > -1) {
                editablePayload[payloadKey] = payload[payloadKey]
            }
        });
        return editablePayload as PayloadType;
    }

    const onSubmit = async (payload: PayloadType, { resetForm }: FormikHelpers<PayloadType>) => {
        let finalPayload = changeEmptyPropsDeep(payload)
        if (formMode === 'create' && createAction) {
            let newItem = await createAction(finalPayload);
            if (newItem) {
                notifications.show({
                    icon: <Icon icon={Icons.warningCircle} width={24} />,
                    color: 'green',
                    autoClose: 4000,
                    title: 'Success!',
                    message: `${formConfig?.title || 'Item'} Created`
                })
                resetForm();
                if (onClose) {
                    onClose(newItem)
                    if (createRedirect) {
                        navigate(`${redirectPath}/${newItem.id}`)
                    }
                }
            } else {
                notifications.show({
                    icon: <Icon icon={Icons.warningCircle} width={24} />,
                    color: 'red',
                    autoClose: 4000,
                    title: 'Opps!',
                    message: `Could Not Create ${formConfig?.title || 'Item'}`
                })
            }
        }
        if (formMode === 'update' && currentItemId && updateAction) {
            let editablePayload = getEditablePayload(finalPayload as PayloadType);
            let updatedItem = await updateAction({ id: currentItemId, _set: editablePayload });
            if (updatedItem) {
                notifications.show({
                    icon: <Icon icon={Icons.warningCircle} width={24} />,
                    color: 'green',
                    autoClose: 4000,
                    title: 'Success!',
                    message: `${formConfig?.title || 'Item'} Updated`
                })
                if (resetAction) resetAction();
                resetForm();
                if (onClose) {
                    onClose(updatedItem)
                    if (updateRedirect && redirectPath) {
                        navigate(redirectPath)
                    }
                }
            } else {
                notifications.show({
                    icon: <Icon icon={Icons.warningCircle} width={24} />,
                    color: 'red',
                    autoClose: 4000,
                    title: 'Opps!',
                    message: `Could Not Update ${formConfig?.title || 'Item'}`
                })
            }
        }
        if (formMode === 'bulkUpdate' && bulkUpdateAction) {
            let editablePayload = getEditablePayload(finalPayload as PayloadType);
            let updatedItems = await bulkUpdateAction(editablePayload);
            if (updatedItems) {
                notifications.show({
                    icon: <Icon icon={Icons.warningCircle} width={24} />,
                    color: 'green',
                    autoClose: 4000,
                    title: 'Success!',
                    message: `${formConfig?.title || 'Item'} Updated`
                })
                if (resetAction) resetAction();
                resetForm();
                if (onClose) {
                    onClose(updatedItems)
                    if (updateRedirect && redirectPath) {
                        navigate(redirectPath)
                    }
                }
            } else {
                notifications.show({
                    icon: <Icon icon={Icons.warningCircle} width={24} />,
                    color: 'red',
                    autoClose: 4000,
                    title: 'Opps!',
                    message: `Could Not Update ${formConfig?.title || 'Item'}`
                })
            }
        }
    }

    const displayErrors = () => {
        if (!errors.length) return null;
        return (
            <Alert title="Error" variant="outline" color="red" mb="md">
                {errors.map((error, key) => {
                    return (
                        <span key={`error-${key}`}>{error.text}</span>
                    )
                })}
            </Alert>
        )
    }

    function onKeyDown(keyEvent: any) {
        if ((keyEvent.charCode || keyEvent.keyCode) === 13) {
            keyEvent.preventDefault();
        }
    }

    if (!formMode || !formConfig) return null;

    return (
        <React.Fragment>

            <Formik
                onKeyDown={onKeyDown}
                initialValues={initialState}
                onSubmit={onSubmit}
                validationSchema={formConfig.validationSchema}
                validateOnBlur={true}
                validateOnMount={false}
                validateOnChange={true}
            >
                {({ values, errors, handleChange, handleBlur, handleSubmit, submitForm, isSubmitting, isValid, dirty, setFieldValue }) => {

                    return (

                        <Box
                            component="form"
                            onSubmit={handleSubmit}
                        >

                            <Grid gutter="xs" style={{ margin: 0 }}>

                                <FieldRenderer
                                    currentItemId={currentItemId}
                                    formConfig={formConfig}
                                    formMode={formMode}
                                    values={values}
                                    errors={errors}
                                    setFieldValue={setFieldValue}
                                    handleBlur={handleBlur}
                                    handleChange={handleChange}
                                />

                            </Grid>

                            <Box>
                                {displayErrors()}
                            </Box>

                            <Group justify="space-between">
                                <Box>
                                    {/* {formMode === 'update' && !hideDelete ? (
                                        <Button
                                            color="red"
                                            onClick={openDeleteModal}
                                        >
                                            Delete
                                        </Button>
                                    ) : null} */}
                                </Box>
                                <Group gap="sm">
                                    {dirty && (
                                        <Button
                                            color="gray"
                                            variant="outline"
                                            onClick={handleClose}>
                                            Clear
                                        </Button>
                                    )}
                                    <Button
                                        color={buttonColor}
                                        onClick={submitForm}
                                        loading={isSubmitting}
                                        disabled={requiredDirty ? !dirty || !isValid : false}
                                        rightSection={<Icon icon={buttonIcon} width={18} />}
                                    >
                                        {buttonText}
                                    </Button>
                                </Group>
                            </Group>


                        </Box>

                    )
                }}

            </Formik>

        </React.Fragment>
    )

}